// src/pages/agent/Dashboard.js
// Modernized agent dashboard: time-aware hero, focused KPI tiles, pipeline
// funnel, awaiting-response queue, and a unified activity feed.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, onSnapshot, orderBy, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { useIsMobile } from '../../lib/useIsMobile';
import { PageHeader, Card, Btn, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT, BORDER, EmptyState } from '../../components/agent/UI';

// Pipeline stages in the order they appear on the funnel. Closed and
// Dead are TERMINAL — they don't appear here. The "Closed this month"
// KPI tile counts them in aggregate but the page itself only surfaces
// active deals so the agent stays focused on what's still in play.
const STAGES = [
  { key: 'New',            color: '#C9A84C' },
  { key: 'Contacted',      color: '#B68437' },
  { key: 'Negotiating',    color: '#9C5A2A' },
  { key: 'Under Contract', color: '#6B1E2E' },
  { key: 'Assigned',       color: '#0F6B3F' }
];

// Status values that bury a lead from the active dashboard surfaces.
// Anything in here is excluded from the pipeline funnel, KPI counts,
// awaiting-response queue, AND the recent activity feed — so once a
// lead is dead/closed it goes silent on the dashboard.
const TERMINAL_STATUSES = new Set(['closed', 'dead', 'lost', 'won', 'sold']);

// How many recent-activity items to show per page (client-side
// pagination). The full feed is capped to 80 items below so admins
// of busy pipelines don't load thousands of rows into memory.
const ACTIVITY_PAGE_SIZE = 8;

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile } = useAgentAuth();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState({ active: 0, contracts: 0, sourced: 0, closed: 0, awaiting: 0, pipelineValue: 0, closedFee: 0 });
  const [stageCounts, setStageCounts] = useState({});
  const [awaiting, setAwaiting] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityPage, setActivityPage] = useState(0);
  const [resolvedPipelineUid, setResolvedPipelineUid] = useState(null);

  useEffect(() => {
    if (!profile?.uid) return;
    (async () => {
      try {
        // Resolve which pipeline to query. Admin: own pipeline (all leads
        // in the company live there). Contract agent: the inviting admin's
        // pipeline — but they only count leads they've claimed
        // (assignedTo.uid == self), so they see ONLY their data.
        let pipelineUid = profile.uid;
        if (profile.role === 'agent') {
          pipelineUid = profile.invitedByUid || null;
          if (!pipelineUid) {
            try {
              const aSnap = await getDocs(query(
                collection(db, 'agents'),
                where('role', '==', 'admin'),
                where('status', '==', 'active'),
                limit(1)
              ));
              if (!aSnap.empty) pipelineUid = aSnap.docs[0].id;
            } catch {}
          }
          if (!pipelineUid) pipelineUid = profile.uid;
        }
        setResolvedPipelineUid(pipelineUid);

        const leadsCol = collection(db, 'agents', pipelineUid, 'leads');
        const all = await getDocs(leadsCol);
        // Shared team model — everyone sees every lead (no per-agent
        // assignment filter).
        const docs = all.docs;
        let active = 0, contracts = 0, sourced = docs.length, closed = 0;
        let pipelineValue = 0, closedFee = 0;
        const counts = {};
        const waiters = [];
        const acts = [];
        const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
        const monthStartMs = monthStart.getTime();

        docs.forEach(d => {
          const data = { id: d.id, ...d.data() };
          const stRaw = data.status || 'New';
          const st = stRaw.toLowerCase();
          const isTerminal = TERMINAL_STATUSES.has(st);

          // Closed/Dead deals contribute to the count + closed-this-month
          // tile but are otherwise hidden from the dashboard.
          if (st === 'closed') {
            closed++;
            const fee = data.calc?.assignmentFee || 0;
            const closedAt = data.updatedAt?.toMillis?.() || 0;
            if (closedAt >= monthStartMs && fee) closedFee += fee;
          }
          // Hide terminal leads from every active surface below.
          if (isTerminal) return;

          counts[stRaw] = (counts[stRaw] || 0) + 1;

          if (['contacted', 'negotiating', 'new'].includes(st)) {
            active++;
            const ask = data.propertyData?.askingPrice || 0;
            if (ask) pipelineValue += ask;
          }
          if (st === 'under contract' || st === 'assigned') contracts++;

          // Awaiting response: last SMS in thread is outbound and >30 min ago
          const thread = data.smsThread || [];
          if (thread.length > 0) {
            const last = thread[thread.length - 1];
            if (last.direction === 'out') {
              const sentMs = last.sentAt?.toMillis?.() || 0;
              const minsAgo = (Date.now() - sentMs) / 60000;
              if (minsAgo >= 30) {
                waiters.push({ id: data.id, ...data, lastSent: sentMs, lastBody: last.body, lastBy: last.agentName });
              }
            }
          }

          // Activity feed: lead created, notes, SMS in/out
          const addrLine = data.address?.line1 || '(no address)';
          if (data.createdAt?.toMillis) {
            acts.push({ type: 'lead_created', at: data.createdAt.toMillis(), leadId: data.id, leadAddress: addrLine });
          }
          (data.notes || []).forEach(n => {
            if (n.createdAt?.toMillis) acts.push({ type: 'note', noteType: n.type, at: n.createdAt.toMillis(), leadId: data.id, leadAddress: addrLine, agentName: n.author?.name, preview: (n.body || '').slice(0, 80) });
          });
          (data.smsThread || []).forEach(m => {
            if (!m.sentAt?.toMillis) return;
            acts.push({
              type: m.direction === 'in' ? 'sms_in' : 'sms_out',
              at: m.sentAt.toMillis(),
              leadId: data.id,
              leadAddress: addrLine,
              agentName: m.agentName,
              preview: (m.body || '').slice(0, 80)
            });
          });
          (data.contracts || []).forEach(c => {
            if (c.signedAt?.toMillis) acts.push({ type: 'contract_signed', at: c.signedAt.toMillis(), leadId: data.id, leadAddress: addrLine, preview: c.type });
            else if (c.sentAt?.toMillis) acts.push({ type: 'contract_sent', at: c.sentAt.toMillis(), leadId: data.id, leadAddress: addrLine, preview: c.type });
          });
        });

        waiters.sort((a, b) => a.lastSent - b.lastSent);
        acts.sort((a, b) => b.at - a.at);

        setStats({ active, contracts, sourced, closed, awaiting: waiters.length, pipelineValue, closedFee });
        setStageCounts(counts);
        setAwaiting(waiters.slice(0, 6));
        // Keep up to 80 most-recent items so we can paginate client-side
        // without re-fetching. Closed/Dead leads were already skipped
        // via the isTerminal early return inside the forEach above, so
        // nothing terminal lands in `acts`.
        setActivity(acts.slice(0, 80));
        setActivityPage(0);
      } catch (e) {
        console.error('dashboard load failed', e);
      }
    })();
  }, [profile?.uid, profile?.role, profile?.invitedByUid]);

  // Earnings have moved off the dashboard — see /agent/finances for
  // the full P&L view (admin) or per-agent payouts (contract agent).

  const fname = (profile?.name || '').split(/\s+/).find(p => p.length > 1 && !p.endsWith('.')) || (profile?.name || '').split(' ')[0] || 'there';
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const totalPipelineCount = Object.values(stageCounts).reduce((a, b) => a + b, 0);

  return (
    <>
      {/* Hero */}
      <div style={{ ...s.hero, ...(isMobile ? s.heroMobile : {}) }}>
        <div>
          <div style={s.heroEyebrow}>{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
          <h1 style={{ ...s.heroTitle, ...(isMobile ? s.heroTitleMobile : {}) }}>
            {greeting}, <em style={{ fontStyle: 'italic', color: BURG }}>{fname}</em>.
          </h1>
          <p style={s.heroSub}>Your wholesale pipeline at a glance.</p>
        </div>
        <div style={{ ...s.heroActions, ...(isMobile ? s.heroActionsMobile : {}) }}>
          <OutreachTile />
          <RentcastMeter />
          <Btn variant="ghost" onClick={() => navigate('/agent/leads')}>View all leads →</Btn>
          <Btn variant="gold" onClick={() => navigate('/agent/leads?new=1')}>+ New Lead</Btn>
        </div>
      </div>

      <RecentResponses pipelineUid={resolvedPipelineUid} />

      {/* KPI tiles */}
      <div style={{ ...s.kpiGrid, ...(isMobile ? s.kpiGridMobile : {}) }}>
        <Kpi
          eyebrow="Active leads"
          value={stats.active}
          sub={stats.active > 0 ? `${totalPipelineCount} total in pipeline` : 'Nothing active yet'}
          accent
        />
        <Kpi
          eyebrow="Awaiting response"
          value={stats.awaiting}
          sub={stats.awaiting > 0 ? `Oldest: ${relativeAgo(awaiting[0]?.lastSent)}` : 'All caught up ✓'}
          alert={stats.awaiting > 0}
        />
        <Kpi
          eyebrow="Pipeline value"
          value={stats.pipelineValue ? compactMoney(stats.pipelineValue) : '$0'}
          sub={stats.active > 0 ? `Asking total · ${stats.active} active` : '—'}
        />
        <Kpi
          eyebrow="Closed this month"
          value={stats.closed}
          sub={stats.closedFee > 0 ? `+${compactMoney(stats.closedFee)} in fees` : 'No fees logged yet'}
        />
      </div>

      {/* Pipeline funnel */}
      {totalPipelineCount > 0 && (
        <Card title="Pipeline" right={<span style={{ fontSize: 11, color: LIGHT, fontStyle: 'italic' }}>{totalPipelineCount} lead{totalPipelineCount === 1 ? '' : 's'} across {STAGES.filter(s => stageCounts[s.key]).length} stage{STAGES.filter(s => stageCounts[s.key]).length === 1 ? '' : 's'}</span>}>
          <div style={{ ...s.funnel, ...(isMobile ? s.funnelMobile : {}) }}>
            {STAGES.map(stage => {
              const n = stageCounts[stage.key] || 0;
              const pct = totalPipelineCount > 0 ? Math.max(6, (n / totalPipelineCount) * 100) : 0;
              return (
                <div
                  key={stage.key}
                  style={{
                    ...s.funnelBar,
                    ...(isMobile ? s.funnelBarMobile : {}),
                    background: n > 0 ? stage.color : '#F4F4F5',
                    width: isMobile ? '100%' : `${pct}%`,
                    opacity: n > 0 ? 1 : 0.4
                  }}
                  onClick={() => navigate(`/agent/leads?status=${encodeURIComponent(stage.key)}`)}
                  title={`${stage.key}: ${n}`}
                >
                  <span style={s.funnelStage}>{stage.key}</span>
                  <span style={s.funnelCount}>{n}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Two-column: Awaiting response + Activity */}
      <div style={{ ...s.twoCol, ...(isMobile ? s.twoColMobile : {}) }}>
        <div>
          <Card
            title={`Awaiting response${stats.awaiting > 0 ? ` (${stats.awaiting})` : ''}`}
            right={<span style={s.cardHint}>Last text sent · no reply yet</span>}
          >
            {awaiting.length === 0 ? (
              <EmptyState
                title="Inbox zero ✓"
                sub="No leads currently awaiting a reply. The moment you send an intro SMS, it'll appear here until the seller responds."
              />
            ) : (
              <ul style={s.list}>
                {awaiting.map(l => (
                  <li key={l.id} style={s.row} onClick={() => navigate(`/agent/leads/${l.id}`)}>
                    <div style={s.rowDot} />
                    <div style={s.rowMain}>
                      <div style={s.rowAddr}>{l.address?.line1 || '(no address)'}</div>
                      <div style={s.rowMeta}>
                        {(l.owners || []).map(o => o.name).filter(Boolean).join(', ') || '—'}
                        {l.lastBy && <> · <strong style={{ color: BURG }}>{l.lastBy}</strong></>}
                      </div>
                      <div style={s.rowSnippet}>"{(l.lastBody || '').slice(0, 90)}{(l.lastBody || '').length > 90 ? '…' : ''}"</div>
                    </div>
                    <div style={s.rowTime}>{relativeAgo(l.lastSent)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
        <div>
          {(() => {
            // Slice the activity list into the current page. Page size is
            // 8 (per user request); we keep the full list in state so
            // Prev/Next is instant.
            const totalPages = Math.max(1, Math.ceil(activity.length / ACTIVITY_PAGE_SIZE));
            const page = Math.min(activityPage, totalPages - 1);
            const start = page * ACTIVITY_PAGE_SIZE;
            const pageItems = activity.slice(start, start + ACTIVITY_PAGE_SIZE);
            return (
              <Card
                title="Recent activity"
                right={
                  activity.length > 0
                    ? <span style={s.cardHint}>{start + 1}–{Math.min(start + ACTIVITY_PAGE_SIZE, activity.length)} of {activity.length}</span>
                    : <a href="/agent/leads" style={s.cardLink}>View all →</a>
                }
              >
                {activity.length === 0 ? (
                  <EmptyState
                    title="No activity yet"
                    sub="Add your first lead and the timeline will start here."
                    action={<Btn variant="gold" onClick={() => navigate('/agent/leads?new=1')}>+ Add a lead</Btn>}
                  />
                ) : (
                  <>
                    <ul style={s.activityList}>
                      {pageItems.map((a, i) => (
                        <li key={start + i} style={s.activityRow} onClick={() => navigate(`/agent/leads/${a.leadId}`)}>
                          <div style={s.activityIcon(a.type)}>{iconFor(a.type)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={s.activityLabel}>{labelFor(a)}</div>
                            <div style={s.activityAddr}>{a.leadAddress}</div>
                            {a.preview && <div style={s.activityPreview}>{a.preview}{a.preview.length >= 80 ? '…' : ''}</div>}
                          </div>
                          <div style={s.rowTime}>{relativeAgo(a.at)}</div>
                        </li>
                      ))}
                    </ul>
                    {totalPages > 1 && (
                      <div style={s.pager}>
                        <button
                          type="button"
                          onClick={() => setActivityPage(p => Math.max(0, p - 1))}
                          disabled={page === 0}
                          style={{ ...s.pagerBtn, ...(page === 0 ? s.pagerBtnDisabled : {}) }}
                        >
                          ← Prev
                        </button>
                        <span style={s.pagerLabel}>Page {page + 1} of {totalPages}</span>
                        <button
                          type="button"
                          onClick={() => setActivityPage(p => Math.min(totalPages - 1, p + 1))}
                          disabled={page >= totalPages - 1}
                          style={{ ...s.pagerBtn, ...(page >= totalPages - 1 ? s.pagerBtnDisabled : {}) }}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            );
          })()}
        </div>
      </div>

      {profile?.role === 'admin' && (
        <Card title="Admin shortcuts">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Btn onClick={() => navigate('/agent/settings#admin')}>Manage Agents</Btn>
            <Btn variant="ghost" onClick={() => navigate('/agent/contracts')}>Contract Templates</Btn>
            <Btn variant="ghost" onClick={() => navigate('/agent/buyers')}>Buyers list</Btn>
          </div>
        </Card>
      )}
    </>
  );
}

const Kpi = ({ eyebrow, value, sub, accent, alert }) => (
  <div style={{ ...s.kpi, ...(accent ? s.kpiAccent : {}), ...(alert ? s.kpiAlert : {}) }}>
    <div style={s.kpiEyebrow}>{eyebrow}</div>
    <div style={s.kpiValue}>{value}</div>
    <div style={s.kpiSub}>{sub}</div>
  </div>
);

// Recent responses strip — every lead with status='Responded' since the
// most recent batch, newest first. Pins to the top of the dashboard so
// hot replies are impossible to miss.
function RecentResponses({ pipelineUid }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!pipelineUid) return;
    // Simple client-side sort so we don't need a compound index.
    const unsub = onSnapshot(
      query(collection(db, 'agents', pipelineUid, 'leads'),
        where('status', '==', 'Responded'), limit(50)),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.respondedAt?.toMillis?.() || 0) - (a.respondedAt?.toMillis?.() || 0));
        setItems(list.slice(0, 8));
      },
      () => setItems([])
    );
    return unsub;
  }, [pipelineUid]);
  if (items.length === 0) return null;
  return (
    <div style={{ background: 'linear-gradient(135deg, #DCFCE7, #FFFBEB)', border: '1px solid #86EFAC', borderRadius: 14, padding: '18px 22px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 15, fontWeight: 700, color: '#166534' }}>
          🔥 Recent responses <span style={{ opacity: 0.7 }}>({items.length})</span>
        </div>
        <div style={{ fontSize: 11, color: '#166534', opacity: 0.75 }}>Reply fast — 24-hour window is where deals happen.</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(l => {
          const owner = (l.owners || [])[0] || {};
          const last = (l.smsThread || []).filter(m => m.direction === 'in').slice(-1)[0];
          return (
            <button key={l.id} type="button" onClick={() => navigate(`/agent/leads/${l.id}`)}
              style={{ textAlign: 'left', background: '#fff', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, fontFamily: "'Inter', sans-serif" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {owner.name || 'Property Owner'} · {l.address?.line1 || 'no address'}
                </div>
                <div style={{ fontSize: 12, color: '#4B5563', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  ↩ {(last?.body || '').slice(0, 120)}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#166534', whiteSpace: 'nowrap', alignSelf: 'center' }}>
                {l.respondedAt?.toDate?.()?.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || ''}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Live daily-outreach tile — reads settings/outreach + rolls the next
// scheduled 8am PT run so the agent always knows what's about to fire
// and what the last batch did.
function OutreachTile() {
  const [cfg, setCfg] = useState(null);
  const [queueCounts, setQueueCounts] = useState({ sent: 0, failed: 0, queued: 0, skipped: 0 });
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'outreach'),
      snap => setCfg(snap.exists() ? snap.data() : null),
      () => setCfg(null)
    );
    return unsub;
  }, []);
  // Live counts from outreachQueue. Filter to today only so old runs
  // don't inflate the numbers.
  useEffect(() => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const unsub = onSnapshot(collection(db, 'outreachQueue'), snap => {
      const c = { sent: 0, failed: 0, queued: 0, skipped: 0 };
      snap.docs.forEach(d => {
        const q = d.data();
        const created = q.createdAt?.toMillis?.() || 0;
        if (created < startOfToday.getTime()) return;
        const s = q.status || 'queued';
        if (c[s] !== undefined) c[s]++;
      });
      setQueueCounts(c);
    }, () => setQueueCounts({ sent: 0, failed: 0, queued: 0, skipped: 0 }));
    return unsub;
  }, []);
  if (!cfg) return null;
  const stats = { ...queueCounts, ...(cfg.lastRunStats || {}), ...queueCounts };
  const enabled = !!cfg.enabled;
  const next = (() => {
    // Next 8:00 AM Pacific. If a batch already ran today, jump straight
    // to tomorrow so the tile doesn't lie about an imminent duplicate run.
    const nowPt = new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
    const ptDate = new Date(nowPt);
    ptDate.setHours(8, 0, 0, 0);
    const lastRunMs = cfg.lastRunAt?.toMillis?.() || 0;
    const startOfTodayPtMs = new Date(nowPt).setHours(0, 0, 0, 0);
    const ranToday = lastRunMs >= startOfTodayPtMs;
    if (ranToday || ptDate.getTime() <= new Date(nowPt).getTime()) {
      ptDate.setDate(ptDate.getDate() + 1);
    }
    const offset = ptDate.getTime() - new Date(nowPt).getTime();
    return new Date(Date.now() + offset);
  })();
  const tone = enabled ? '#166534' : '#6B7280';
  const bg = enabled ? '#DCFCE7' : '#F3F4F6';
  const bd = enabled ? '#86EFAC' : '#E5E7EB';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderRadius: 10, background: bg, color: tone, border: `1px solid ${bd}`, fontSize: 12, fontWeight: 500, fontFamily: "'Inter', sans-serif", lineHeight: 1.3 }}
      title="Daily outreach batch — Settings › Daily outreach batch">
      <span style={{ fontSize: 15 }}>{enabled ? '▶' : '⏸'}</span>
      <div>
        <div><strong>Daily outreach:</strong> {enabled ? 'On' : 'Off'} · {queueCounts.sent} sent · {queueCounts.queued} pending{queueCounts.failed > 0 ? ` · ${queueCounts.failed} failed` : ''} today</div>
        <div style={{ fontSize: 11, opacity: 0.75 }}>
          Next: {next.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · 8am-11am PT window
        </div>
      </div>
    </div>
  );
}

// Live RentCast usage meter — reads settings/rentcastUsage which the
// pullComps Cloud Function bumps once per API request (retries included).
// Turns amber at 70% and red at 90% so we notice before the quota bites.
function RentcastMeter() {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'rentcastUsage'),
      snap => setUsage(snap.exists() ? snap.data() : { used: 0, cycleLimit: 50 }),
      () => setUsage({ used: 0, cycleLimit: 50 })
    );
    return unsub;
  }, []);
  if (!usage) return null;
  const used = usage.used || 0;
  const limitN = usage.cycleLimit || 50;
  const pct = limitN ? used / limitN : 0;
  const reset = usage.cycleEnd?.toDate?.();
  const resetLabel = reset ? reset.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';
  const tone = pct >= 0.9 ? 'red' : pct >= 0.7 ? 'amber' : 'ok';
  const toneStyles = {
    ok:    { bg: '#F3F4F6', fg: '#374151', bd: '#E5E7EB' },
    amber: { bg: '#FEF3C7', fg: '#78350F', bd: '#FDE68A' },
    red:   { bg: '#FEE2E2', fg: '#991B1B', bd: '#FCA5A5' }
  }[tone];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, background: toneStyles.bg, color: toneStyles.fg, border: `1px solid ${toneStyles.bd}`, fontSize: 12, fontWeight: 500, fontFamily: "'Inter', sans-serif" }}
      title={`RentCast API — ${used} of ${limitN} used this cycle. Resets ${reset ? reset.toLocaleString() : 'next cycle'}. Retries count.`}
    >
      🏠 RentCast <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{used}/{limitN}</strong>
      <span style={{ opacity: 0.7 }}>· resets {resetLabel}</span>
    </div>
  );
}

function relativeAgo(ms) {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  return `${w}w ago`;
}

function compactMoney(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function iconFor(type) {
  return ({
    lead_created:    '＋',
    note:            '✎',
    sms_out:         '⇧',
    sms_in:          '⇩',
    contract_sent:   '✉',
    contract_signed: '✓'
  })[type] || '●';
}

function labelFor(a) {
  switch (a.type) {
    case 'lead_created':    return 'New lead created';
    case 'note':            return `Note · ${a.noteType || 'Other'}`;
    case 'sms_out':         return `SMS sent${a.agentName ? ' by ' + a.agentName : ''}`;
    case 'sms_in':          return 'Inbound reply';
    case 'contract_sent':   return `Contract sent · ${a.preview || ''}`;
    case 'contract_signed': return `✓ Contract signed · ${a.preview || ''}`;
    default: return 'Activity';
  }
}

const s = {
  hero: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, padding: '4px 0 28px', flexWrap: 'wrap' },
  heroEyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 },
  heroTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 38, fontWeight: 500, color: BURG_DK, letterSpacing: '-0.5px', lineHeight: 1.1, margin: 0 },
  heroSub: { fontSize: 14, color: MID, marginTop: 8, marginBottom: 0, lineHeight: 1.55 },
  heroActions: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 },
  kpi: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '20px 22px', position: 'relative', overflow: 'hidden' },
  kpiAccent: { background: GOLD_PALE, borderColor: 'rgba(201,168,76,0.4)' },
  kpiAlert: { background: '#FFF8E6', borderColor: '#C9A84C', borderLeft: `3px solid ${BURG}` },
  kpiEyebrow: { fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: LIGHT, marginBottom: 10 },
  kpiValue: { fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 600, color: BURG_DK, lineHeight: 1, marginBottom: 8, letterSpacing: '-1px' },
  kpiSub: { fontSize: 11, color: MID, lineHeight: 1.4 },
  funnel: { display: 'flex', gap: 4, height: 56, alignItems: 'stretch' },
  funnelBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', borderRadius: 4, color: '#fff', cursor: 'pointer', transition: 'transform 0.15s ease', minWidth: 80 },
  funnelStage: { fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  funnelCount: { fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600 },
  twoCol: { display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 20, alignItems: 'flex-start' },
  cardHint: { fontSize: 11, color: LIGHT, fontStyle: 'italic' },
  cardLink: { fontSize: 11, color: BURG, textDecoration: 'none' },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  row: { padding: '12px 4px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' },
  rowDot: { width: 6, height: 6, borderRadius: '50%', background: GOLD, marginTop: 8, flexShrink: 0, boxShadow: '0 0 0 3px rgba(201,168,76,0.18)' },
  rowMain: { flex: 1, minWidth: 0 },
  rowAddr: { fontSize: 13, color: BURG_DK, fontWeight: 500, marginBottom: 2 },
  rowMeta: { fontSize: 11, color: LIGHT, marginBottom: 4 },
  rowSnippet: { fontSize: 12, color: MID, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowTime: { fontSize: 11, color: BURG, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 },
  activityList: { listStyle: 'none', padding: 0, margin: 0 },
  activityRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 4px', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer' },
  activityIcon: (type) => ({ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0, background: ({ lead_created: GOLD_PALE, note: '#F2EBE0', sms_out: '#E7F5EE', sms_in: '#E0F2FE', contract_sent: GOLD_PALE, contract_signed: '#E7F5EE' })[type] || CREAM, color: ({ sms_out: '#0F6B3F', sms_in: '#1E40AF', contract_signed: '#0F6B3F' })[type] || BURG_DK }),
  activityLabel: { fontSize: 12, fontWeight: 500, color: BURG_DK },
  activityAddr: { fontSize: 11, color: LIGHT, marginTop: 1 },
  activityPreview: { fontSize: 11, color: MID, fontStyle: 'italic', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 },

  // Recent-activity pagination controls
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 4px 4px', marginTop: 4, borderTop: `1px solid ${BORDER}` },
  pagerBtn: { background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '6px 14px', fontSize: 12, color: BURG, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 500 },
  pagerBtnDisabled: { color: LIGHT, cursor: 'not-allowed', opacity: 0.5 },
  pagerLabel: { fontSize: 11, color: LIGHT, letterSpacing: '0.04em' },

  // Mobile overrides — keyed in via useIsMobile() so the same component
  // collapses cleanly on a phone without duplicating the JSX tree.
  heroMobile: { flexDirection: 'column', alignItems: 'stretch', gap: 16, padding: '4px 0 18px' },
  heroTitleMobile: { fontSize: 28, lineHeight: 1.15 },
  heroActionsMobile: { width: '100%' },
  kpiGridMobile: { gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 },
  funnelMobile: { flexDirection: 'column', height: 'auto', gap: 6 },
  funnelBarMobile: { minHeight: 36, minWidth: 0, padding: '8px 12px' },
  twoColMobile: { gridTemplateColumns: '1fr', gap: 14 }
};

